import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, Users } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FeaturedFigures } from "@/components/site/FeaturedFigures";
import { useLanguage } from "@/context/LanguageContext";
import { NOTABLE } from "@/data/notableMcDonalds";
import { categorize, CATEGORY_ORDER, DEFAULT_CATEGORY_LABELS } from "@/data/notableCategories";

// Pre-tag every namebearer with a profession category once (facts are static).
// Stable, globally-unique id per entry so distinct people who share a name +
// birth year (e.g. two "Peter McDonald, *1973") still render with unique keys.
const CATEGORIZED = NOTABLE.map((p, idx) => ({ ...p, cat: categorize(p.desc), _id: idx }));

export default function NotableMcDonalds() {
  const { t } = useLanguage();
  const n = t.notable;
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("all");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const catLabel = (k) => (n.categories && n.categories[k]) || DEFAULT_CATEGORY_LABELS[k];

  const catCounts = useMemo(() => {
    const c = {};
    for (const p of CATEGORIZED) c[p.cat] = (c[p.cat] || 0) + 1;
    return c;
  }, []);
  const availableCats = CATEGORY_ORDER.filter((k) => catCounts[k]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CATEGORIZED.filter((p) => {
      if (activeCat !== "all" && p.cat !== activeCat) return false;
      if (!q) return true;
      return p.name.toLowerCase().includes(q) || p.desc.toLowerCase().includes(q) || (p.life || "").includes(q);
    });
  }, [query, activeCat]);

  const groups = useMemo(() => {
    const map = {};
    for (const p of filtered) {
      const letter = p.name[0].toUpperCase();
      (map[letter] = map[letter] || []).push(p);
    }
    return Object.keys(map)
      .sort()
      .map((letter) => ({ letter, people: map[letter] }));
  }, [filtered]);

  return (
    <div className="relative min-h-screen bg-clan-bg text-clan-ink">
      <div className="grain-overlay" aria-hidden="true" />

      <Header />

      <main className="mx-auto max-w-6xl px-6 pb-28 pt-32 lg:px-10 lg:pt-40">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <span className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.28em] text-clan-accent">
            {n.overline}
          </span>
          <h1 className="mt-6 font-serif text-4xl font-light leading-[1.02] tracking-tight text-clan-ink sm:text-5xl lg:text-6xl pearl">
            {n.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-clan-muted">{n.subtitle}</p>
        </motion.div>

        {/* Featured spotlight */}
        <FeaturedFigures />

        {/* Search + count */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="relative w-full sm:max-w-md">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-clan-muted" />
            <input
              data-testid="notable-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={n.search}
              className="w-full rounded-md border border-clan-ink/10 bg-clan-ink/[0.04] py-3 pl-11 pr-4 font-sans text-clan-ink placeholder:text-clan-muted/70 transition-colors duration-300 focus:border-clan-accent focus:bg-clan-ink/[0.06] focus:outline-none focus:ring-1 focus:ring-clan-accent"
            />
          </div>
          <div
            data-testid="notable-count"
            className="flex items-center gap-3 self-start rounded-md border border-clan-accent/30 bg-clan-accent/5 px-5 py-2.5 sm:self-auto"
          >
            <Users className="h-4 w-4 text-clan-accent" />
            <span className="font-mono text-sm text-clan-ink">
              <span className="text-clan-accent">{filtered.length}</span> {n.count}
            </span>
          </div>
        </motion.div>

        {/* Profession filter */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.22 }}
          className="mt-8"
        >
          <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.22em] text-clan-muted/80">{n.filter}</p>
          <div data-testid="notable-filters" className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="notable-filter-all"
              onClick={() => setActiveCat("all")}
              className={`rounded-md border px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                activeCat === "all"
                  ? "border-clan-accent bg-clan-accent/15 text-clan-ink"
                  : "border-clan-ink/12 text-clan-muted hover:border-clan-accent/50 hover:text-clan-ink"
              }`}
            >
              {n.all} <span className="text-clan-accent">{CATEGORIZED.length}</span>
            </button>
            {availableCats.map((k) => (
              <button
                key={k}
                type="button"
                data-testid={`notable-filter-${k}`}
                onClick={() => setActiveCat(k)}
                className={`rounded-md border px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                  activeCat === k
                    ? "border-clan-accent bg-clan-accent/15 text-clan-ink"
                    : "border-clan-ink/12 text-clan-muted hover:border-clan-accent/50 hover:text-clan-ink"
                }`}
              >
                {catLabel(k)} <span className="text-clan-accent">{catCounts[k]}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Directory */}
        <div className="mt-14">
          {groups.length === 0 ? (
            <div data-testid="notable-empty" className="rounded-lg border border-dashed border-clan-ink/15 p-14 text-center">
              <p className="font-serif text-2xl italic text-clan-muted">{n.empty}</p>
            </div>
          ) : (
            <div className="flex flex-col gap-12">
              {groups.map((g) => (
                <section key={g.letter} data-testid={`notable-group-${g.letter}`}>
                  <div className="mb-5 flex items-center gap-4">
                    <span className="font-serif text-3xl text-clan-accent">{g.letter}</span>
                    <span className="h-px flex-1 bg-clan-ink/[0.06]" />
                  </div>
                  <div className="grid gap-x-10 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
                    {g.people.map((p) => (
                      <div
                        key={p._id}
                        data-testid="notable-entry"
                        className="group border-l border-clan-ink/10 pl-4 transition-colors duration-300 hover:border-clan-accent"
                      >
                        <p className="font-serif text-lg leading-snug text-clan-ink">
                          {p.name}
                          {p.life && <span className="ml-2 font-mono text-xs tracking-wide text-clan-accent">{p.life}</span>}
                        </p>
                        <p className="mt-1 text-sm leading-relaxed text-clan-muted">{p.desc}</p>
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        <p className="mt-20 max-w-2xl border-t border-clan-ink/10 pt-8 text-xs leading-relaxed text-clan-muted/70">
          {n.disclaimer}
        </p>
      </main>

      <Footer />
    </div>
  );
}
